import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useListOffers } from '@/features/offers/api/list-offers';
import { useListServices } from '@/features/organization-services';
import { cn } from '@/lib/utils';
import { offerStateLabels } from '@borradh-workspace/api-client/types';
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Loader2,
  Sparkles,
  XIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useGetAd } from '../../api/get-ad';
import { useGetAdPromotionCheck } from '../../api/get-ad-promotion-check';
import { type Ad, adLinkSourceLabels } from '../../api/types';
import { buildAdLinksPayload, useUpdateAd } from '../../api/update-ad';

/** Select value standing for "no promotion" (Radix forbids an empty value). */
const NO_PROMOTION = '__none__';

/** The subtle "the matcher guessed this" marker. */
function MatchedAutomaticallyBadge() {
  return (
    <Badge
      variant="outline"
      className="gap-1 px-1.5 py-0 text-[10px] font-normal text-muted-foreground"
      title="Borradh linked this from the ad's text and creative. Check it's right."
    >
      <Sparkles className="size-3" aria-hidden />
      {adLinkSourceLabels.auto}
    </Badge>
  );
}

export interface AdLinksSectionProps {
  /** The ad as the caller already has it; refreshed from `GET /meta-ads/:id`. */
  ad: Ad;
  className?: string;
}

/**
 * The services and promotion an ad is linked to, editable for EVERY ad —
 * imported ones included, which is where most links are missing or were
 * guessed by the matcher.
 *
 * Each change saves on its own, as a links-only update: the payload carries
 * `serviceIds` or `offerId` and nothing else, so it never reaches Meta, never
 * rebuilds a creative and never sends a live ad back to review. That is why
 * this lives outside the copy form and its "send for review" confirm.
 */
export function AdLinksSection({
  ad: initialAd,
  className,
}: AdLinksSectionProps) {
  // The panel may hold a list-row snapshot; the detail read carries each
  // service's link source and is invalidated by the update below.
  const { ad: fetchedAd } = useGetAd(initialAd.id);
  const ad = fetchedAd ?? initialAd;
  const { check: promotionCheck } = useGetAdPromotionCheck(initialAd.id);
  const promotionMismatch =
    promotionCheck?.verdict === 'contradicts' ||
    promotionCheck?.verdict === 'out_of_date' ||
    promotionCheck?.verdict === 'no_promotion'
      ? promotionCheck
      : null;

  // Archived too: a service archived after it was linked must still show.
  const {
    services: catalogue,
    isLoading: isLoadingServices,
    isError: isServicesError,
    refetch: refetchServices,
  } = useListServices({ isActive: undefined });
  const {
    offers,
    isLoading: isLoadingOffers,
    isError: isOffersError,
    refetch: refetchOffers,
  } = useListOffers({ limit: 100 });
  const { executeAsync, isExecuting } = useUpdateAd(false, true);

  const linkedServices = ad.services ?? [];
  const linkedServiceIds = linkedServices.map((s) => s.id);
  const servicesAutoMatched = linkedServices.some(
    (s) => s.linkSource === 'auto'
  );

  const [pickerOpen, setPickerOpen] = useState(false);
  /** Ticks staged while the picker is open; saved when it closes. */
  const [draftServiceIds, setDraftServiceIds] = useState<string[] | null>(null);
  const pickedIds = draftServiceIds ?? linkedServiceIds;

  // Archived services stay pickable only while already linked, mirroring the
  // server's rule, so a clinic can keep a past link without being offered
  // retired services for new ones.
  const pickable = useMemo(
    () =>
      catalogue.filter((s) => s.isActive || linkedServiceIds.includes(s.id)),
    [catalogue, linkedServiceIds]
  );

  const linkedOffer = ad.offerId
    ? offers.find((o) => o.id === ad.offerId)
    : undefined;

  const saveServices = async (serviceIds: string[]) => {
    const same =
      serviceIds.length === linkedServiceIds.length &&
      serviceIds.every((id) => linkedServiceIds.includes(id));
    if (same) return;
    try {
      await executeAsync({
        adId: ad.id,
        data: buildAdLinksPayload({ serviceIds }),
      });
      toast.success('Services updated');
    } catch {
      // The hook's onError toast says what failed.
    }
  };

  const saveOffer = async (value: string) => {
    const offerId = value === NO_PROMOTION ? null : value;
    if (offerId === (ad.offerId ?? null)) return;
    try {
      await executeAsync({
        adId: ad.id,
        data: buildAdLinksPayload({ offerId }),
      });
      toast.success(offerId ? 'Promotion linked' : 'Promotion removed');
    } catch {
      // The hook's onError toast says what failed.
    }
  };

  const onPickerOpenChange = (open: boolean) => {
    setPickerOpen(open);
    if (open) {
      setDraftServiceIds(linkedServiceIds);
      return;
    }
    const draft = draftServiceIds;
    setDraftServiceIds(null);
    if (draft) void saveServices(draft);
  };

  const toggleService = (id: string) => {
    setDraftServiceIds((current) => {
      const base = current ?? linkedServiceIds;
      return base.includes(id)
        ? base.filter((existing) => existing !== id)
        : [...base, id];
    });
  };

  return (
    <section
      className={cn('space-y-4 rounded-lg border p-3', className)}
      aria-label="What this ad promotes"
      data-testid="ad-links-section"
    >
      <div className="space-y-1">
        <h3 className="text-sm font-medium">What this ad promotes</h3>
        <p className="text-xs text-muted-foreground">
          Used by Claire and your reports. Changing these doesn&apos;t touch the
          ad on Meta.
        </p>
      </div>

      {/* Services */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" id="ad-links-services-label">
            Services
          </span>
          {servicesAutoMatched && <MatchedAutomaticallyBadge />}
          {isExecuting && (
            <Loader2
              className="size-3.5 animate-spin text-muted-foreground"
              aria-label="Saving"
            />
          )}
        </div>

        {linkedServices.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {linkedServices.map((service) => (
              <Badge
                key={service.id}
                variant="secondary"
                className="gap-1 pr-1 font-normal"
              >
                {service.name}
                <button
                  type="button"
                  className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  aria-label={`Remove ${service.name}`}
                  disabled={isExecuting}
                  onClick={() =>
                    void saveServices(
                      linkedServiceIds.filter((id) => id !== service.id)
                    )
                  }
                >
                  <XIcon className="size-3" />
                </button>
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No services linked yet.
          </p>
        )}

        {isLoadingServices ? (
          <Skeleton className="h-9 w-full" />
        ) : isServicesError ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-dashed p-2 text-xs">
            <span className="text-destructive">
              Couldn&apos;t load your services.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetchServices()}
            >
              Try again
            </Button>
          </div>
        ) : pickable.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            You haven&apos;t added any services yet.
          </p>
        ) : (
          <Popover open={pickerOpen} onOpenChange={onPickerOpenChange}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-expanded={pickerOpen}
                aria-labelledby="ad-links-services-label"
                disabled={isExecuting}
                className="w-full justify-between font-normal"
              >
                <span className="text-muted-foreground">
                  {linkedServices.length > 0
                    ? 'Change services'
                    : 'Choose services'}
                </span>
                <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <Command>
                <CommandInput
                  placeholder="Search services..."
                  className="h-9"
                />
                <CommandList>
                  <CommandEmpty>No services found.</CommandEmpty>
                  <CommandGroup>
                    {pickable.map((service) => {
                      const picked = pickedIds.includes(service.id);
                      return (
                        <CommandItem
                          key={service.id}
                          value={`${service.name} ${service.id}`}
                          onSelect={() => toggleService(service.id)}
                          aria-selected={picked}
                        >
                          <span className="truncate">{service.name}</span>
                          <Check
                            className={cn(
                              'ml-auto size-4',
                              picked ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
              <div className="flex justify-end border-t p-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => onPickerOpenChange(false)}
                >
                  Done
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Promotion */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" id="ad-links-promotion-label">
            Promotion
          </span>
          {ad.offerId && ad.offerLinkSource === 'auto' && (
            <MatchedAutomaticallyBadge />
          )}
        </div>

        {isLoadingOffers ? (
          <Skeleton className="h-9 w-full" />
        ) : isOffersError ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-dashed p-2 text-xs">
            <span className="text-destructive">
              Couldn&apos;t load your promotions.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetchOffers()}
            >
              Try again
            </Button>
          </div>
        ) : (
          <Select
            value={ad.offerId ?? NO_PROMOTION}
            onValueChange={(value) => void saveOffer(value)}
            disabled={isExecuting}
          >
            <SelectTrigger
              size="sm"
              className="w-full"
              aria-labelledby="ad-links-promotion-label"
            >
              <SelectValue placeholder="No promotion" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PROMOTION}>No promotion</SelectItem>
              {/* A linked promotion outside the first page still shows. */}
              {ad.offerId && !linkedOffer && (
                <SelectItem value={ad.offerId}>Linked promotion</SelectItem>
              )}
              {offers.map((offer) => (
                <SelectItem key={offer.id} value={offer.id}>
                  {offer.name}
                  {offer.state !== 'active' && (
                    <span className="text-muted-foreground">
                      {' '}
                      · {offerStateLabels[offer.state]}
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {!isLoadingOffers && !isOffersError && offers.length === 0 && (
          <p className="text-xs text-muted-foreground">
            You haven&apos;t created any promotions yet.
          </p>
        )}

        {promotionMismatch && (
          <div
            className="flex gap-2 rounded-md border border-amber-200/70 bg-amber-50/60 p-2 text-xs leading-relaxed text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
            role="status"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <div className="space-y-1">
              <p>
                <strong>
                  {promotionMismatch.verdict === 'out_of_date'
                    ? 'This ad is out of date.'
                    : promotionMismatch.verdict === 'no_promotion'
                      ? "This ad's offer isn't set up as a promotion."
                      : "This ad doesn't match your promotion."}
                </strong>{' '}
                {promotionMismatch.summary}
              </p>
              <p className="text-amber-800/80 dark:text-amber-200/80">
                {promotionMismatch.verdict === 'no_promotion'
                  ? "Claire won't quote your full price as the offer to people from this ad. Add the promotion so she can quote it."
                  : "Claire won't quote the mismatched promotion to people from this ad. Update the promotion or the ad so they agree."}
              </p>
            </div>
          </div>
        )}

        {linkedOffer?.state === 'draft' && (
          <div
            className="flex gap-2 rounded-md border border-amber-200/70 bg-amber-50/60 p-2 text-xs leading-relaxed text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
            role="status"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <p>
              <strong>{linkedOffer.name}</strong> is still a draft. Make it
              active on the Promotions page so clients can actually claim it.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
